import { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { api } from "../../api";
import { X } from "../../compose/Icon";
import { BrandedCanvasPreview } from "./BrandedCanvasPreview";

/**
 * `<PdfPreviewModal>` — a portaled overlay (mirrors the workbench's
 * `<SideBySideDiff>` / `<SectionHandlePopover>` pattern) that renders the
 * Cerebr8-BRANDED document preview IN-PAGE on a `<canvas>` via
 * `<BrandedCanvasPreview>` (design §3.4). The body is laid out by the SAME
 * Zig→WASM engine that emits the branded PDF (`@norkron/web-page-layout` over
 * `pdfz-layout.wasm`) and the Cerebr8 cover + running header/footer are drawn
 * from the plate spec — so the preview renders in EVERY browser regardless of
 * its PDF-viewer setting.
 *
 * This deliberately does NOT use an `<iframe src=…pdf>`: an iframe only previews
 * inline when the BROWSER owns a PDF viewer, and silently DOWNLOADS (or blanks)
 * when the user chose "download PDFs instead of opening them" or is on a
 * webview with no inline viewer (`navigator.pdfViewerEnabled === false`) — the
 * exact "preview not working" symptom. The canvas renderer has no such
 * dependency: it rasterizes glyphs itself at pdfz's own positions. Closes on
 * Escape / outside-click. The header offers the two branded downloads.
 */
export function PdfPreviewModal({
  docId,
  title,
  onClose,
}: {
  docId: string;
  title?: string;
  onClose: () => void;
}) {
  const close = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [close]);

  const downloadPdf = api.exportUrl(docId, "pdf", { template: "cerebr8" });
  const downloadDocx = api.exportUrl(docId, "docx", { template: "cerebr8" });

  return createPortal(
    <div
      className="wb-pdfpreview__backdrop"
      data-testid="pdf-preview-modal"
      onMouseDown={(e) => {
        // Outside-click (on the backdrop itself) closes; clicks inside the panel
        // do not bubble to here.
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        className="wb-pdfpreview__panel"
        role="dialog"
        aria-modal="true"
        aria-label={title ? `Preview — ${title}` : "Document preview"}
      >
        <div className="wb-pdfpreview__head">
          <span className="wb-pdfpreview__title">
            Preview{title ? ` — ${title}` : ""}
          </span>
          <div className="wb-pdfpreview__actions">
            <a
              className="wb-icon-btn"
              data-testid="pdf-preview-download-pdf"
              href={downloadPdf}
              download
            >
              PDF
            </a>
            <a
              className="wb-icon-btn"
              data-testid="pdf-preview-download-docx"
              href={downloadDocx}
              download
            >
              DOCX
            </a>
            <button
              type="button"
              className="wb-icon-btn"
              data-testid="pdf-preview-close"
              aria-label="Close preview"
              onClick={close}
            >
              <X size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>
        <BrandedCanvasPreview docId={docId} title={title} />
      </div>
    </div>,
    document.body,
  );
}
